import { createRoot } from "react-dom/client"
import type { MutableRefObject } from "react"
import type { WebViewerInstance } from "@pdftron/webviewer"
import type { TViewerMode } from "store/viewer.store"
import type { TQuotationZoomDirection } from "store/viewer.store"
import type { TFigureShapeType } from "store/figureHighlight.store"
import { setToolModeOnAllPanes, type TViewerTool } from "./viewerTools"
import { disposeRoot, type RootEntry } from "./shadowPortal"
import { CustomWebViewerHeader } from "../CustomWebViewerHeader/CustomWebViewerHeader"

// Toolbar groups hidden in preview mode so the read-only preview matches the Documents-tab
// DocumentModal (no annotate/edit affordances).
export const PREVIEW_DISABLED_ELEMENTS = [
	"toolbarGroup-Annotate",
	"toolbarGroup-Insert",
	"toolbarGroup-Edit",
	"toolbarGroup-FillAndSign",
	"toolbarGroup-Forms",
	"notesPanelToggle",
]

// Chrome Apryse renders when MultiViewerMode is entered (its side-by-side mode doubles as the
// "Compare" feature host). We use the mode purely for split-screen viewing of the SAME drawing —
// never the semantic text diff — so the compare panel/toggles are suppressed. REQ-399.
export const COMPARE_DISABLED_ELEMENTS = [
	"comparePanel",
	"comparePanelToggle",
	"comparePanelToggleButton",
	"comparisonToggleButton",
	"toggleCompareModeButton",
]

// Everything `applyViewerHeaderAndTools` reads from the orchestrator: the one-time-setup guards,
// the custom-header React root, the current mode, and the callbacks/setters it toggles. Passed in
// so this stays a plain module function while the state itself lives in the hook.
export type ApplyViewerModeContext = {
	modeRef: MutableRefObject<TViewerMode>
	customHeaderAddedRef: MutableRefObject<boolean>
	previewFeedbackButtonAddedRef: MutableRefObject<boolean>
	headerEntryRef: MutableRefObject<RootEntry | null>
	onClose?: () => void
	openQuotationFeedback: () => void
	openQuotationSettings: () => void
	openFeedbackPanel: () => void
	// Opens the keyboard-shortcuts panel from the header's help menu (REQ-359). Referentially stable
	// for the same reason as the callbacks below: the header renders once and captures its props.
	openKeyboardShortcuts: () => void
	// Must be identity-stable: the header is rendered once (see the customHeaderAddedRef guard
	// below), so it captures whatever callback the FIRST call passed. `useResultCsvDownload` returns
	// one that reads the quotation id and names from refs for exactly this reason.
	downloadResultCsv: () => Promise<void>
	// Same referential-stability requirement as downloadResultCsv above, for the on-demand annotated
	// result PDF (#1401). `useResultPdfDownload` returns one for the same ref-reading reason.
	downloadResultPdf: () => Promise<void>
	// Same referential-stability requirement as downloadResultCsv above, for the on-demand Quotation
	// Details Word file (#1448). `useDetailsWordDownload` returns one for the same ref-reading reason.
	downloadDetailsWord: () => Promise<void>
	setIsPanelOpen: (open: boolean) => void
	setIsReferencePanelOpen: (open: boolean) => void
	// Turns split screen on/off from the header's layout menu (REQ-399; #1311 decision D10). Must be
	// referentially stable across renders — the header's render() callback captures it once (see the
	// one-time-setup block below). Omitted when the feature flag is off, which hides the split
	// control and disables the two pane-count rows.
	onSetSplitActive?: (active: boolean) => void
	// Split-view menu's "Close left view" / "Close right view" rows: each discards one pane and
	// keeps the OTHER pane's page/zoom/scroll. Same referential-stability requirement as
	// onSetSplitActive, for the same reason. Omitted alongside it when the feature flag is off.
	onCloseLeftView?: () => void
	onCloseRightView?: () => void
	// "Dual view in new tab" / "Return view back" (#1430) — the second view in its own browser
	// window, and the way back. Same referential-stability requirement as onSetSplitActive; both are
	// omitted together when VITE_POP_OUT_WINDOW=off, which removes the row.
	onPopOut?: () => void
	onReturnViewBack?: () => void
	onToolChange: (tool: TViewerTool, zoomDirection?: TQuotationZoomDirection) => void
	onToggleRulerTool: () => void
	// "+ Add → Elevation reference" (REQ-292). Like onSetSplitActive, it must be referentially stable:
	// the header's render() callback captures it once.
	onAddElevationReference: () => void
	// The rest of the + Add menu: elevation callout (REQ-294), countertop (REQ-298) and cabinet
	// (REQ-293). Referentially stable for the same reason.
	onAddCountertop: () => void
	onAddElevationCallout: () => void
	onAddCabinet: () => void
	// "+ Add → Other" (REQ-299). Referentially stable for the same reason.
	onAddOther: () => void
	// "+ Add → Custom object" (#1371). Referentially stable for the same reason.
	onAddCustom: () => void
	// REQ-400 figure highlight tool: arms/leaves the Shapes tool. Referentially stable for the same
	// reason as the rest of this context — the header's render() callback captures it once.
	onArmShapeTool: (shape: TFigureShapeType) => void
	onDisarmShapeTool: () => boolean
	// REQ-395/396 note tool: arms/disarms Apryse's native FreeTextAnnotation create tool + the
	// tool-options row's close button. Referentially stable for the same reason as the rest of this
	// context — the header's render() callback captures it once.
	onToggleNoteTool: () => void
	onCloseNoteToolbar: () => void
	// Stable — see the header prop's own comment.
	onToolOptionsRowToggle: () => void
	// REQ-400 #1324 freehand highlight tool — a single toggle, unlike the shape tool's arm/disarm
	// pair: its toolbar button is visibly active while armed and clicking it again exits.
	onToggleFreehandTool: () => void
}

// Configure the header + tools for the active mode on the SINGLE shared instance. Called once at
// init and again whenever `mode` flips: the first call registers the custom editor header
// ALONGSIDE the default one (never replacing it) and disables Apryse's built-in popups + copy/paste
// hotkeys; subsequent calls only toggle each header's visibility + the active tool. See #1119.
export const applyViewerHeaderAndTools = (instance: WebViewerInstance, ctx: ApplyViewerModeContext) => {
	const {
		modeRef,
		customHeaderAddedRef,
		previewFeedbackButtonAddedRef,
		headerEntryRef,
		onClose,
		openQuotationFeedback,
		openQuotationSettings,
		openFeedbackPanel,
		openKeyboardShortcuts,
		downloadResultCsv,
		downloadResultPdf,
		downloadDetailsWord,
		setIsPanelOpen,
		setIsReferencePanelOpen,
		onSetSplitActive,
		onCloseLeftView,
		onCloseRightView,
		onPopOut,
		onReturnViewBack,
		onToolChange,
		onToggleRulerTool,
		onAddElevationReference,
		onAddCountertop,
		onAddElevationCallout,
		onAddCabinet,
		onAddOther,
		onAddCustom,
		onArmShapeTool,
		onDisarmShapeTool,
		onToggleNoteTool,
		onCloseNoteToolbar,
		onToolOptionsRowToggle,
		onToggleFreehandTool,
	} = ctx
	const { UI } = instance
	const { Tools } = instance.Core

	// One-time setup: build the editor's custom header and add the preview "Share feedback"
	// button to the default header, then register the custom header ALONGSIDE the default one
	// via addModularHeaders (NOT setModularHeaders([custom]), which replaces — destroying the
	// default header so a later switch back to preview could not restore it; that was the
	// "editor header missing after preview" / blank-header bug). From here on, switching modes
	// only toggles each header's visibility. #1119
	if (!customHeaderAddedRef.current) {
		const headerBlock = {
			type: "customElement",
			dataElement: "customHeaderBlock",
			render: () => {
				const container = document.createElement("div")
				disposeRoot(headerEntryRef)
				headerEntryRef.current = { root: createRoot(container), container }
				headerEntryRef.current.root.render(
					<CustomWebViewerHeader
						instance={instance}
						onToolChange={onToolChange}
						onToggleRulerTool={onToggleRulerTool}
						onClose={onClose}
						onShareFeedback={openQuotationFeedback}
						onOpenKeyboardShortcuts={openKeyboardShortcuts}
						onSettings={openQuotationSettings}
						onDownloadResultCsv={downloadResultCsv}
						onDownloadResultPdf={downloadResultPdf}
						onDownloadDetailsWord={downloadDetailsWord}
						onSetSplitActive={onSetSplitActive}
						onCloseLeftView={onCloseLeftView}
						onCloseRightView={onCloseRightView}
						onPopOut={onPopOut}
						onReturnViewBack={onReturnViewBack}
						onAddElevationReference={onAddElevationReference}
						onAddCountertop={onAddCountertop}
						onAddElevationCallout={onAddElevationCallout}
						onAddCabinet={onAddCabinet}
						onAddOther={onAddOther}
						onAddCustom={onAddCustom}
						onArmShapeTool={onArmShapeTool}
						onDisarmShapeTool={onDisarmShapeTool}
						onToggleNoteTool={onToggleNoteTool}
						onCloseNoteToolbar={onCloseNoteToolbar}
						onToolOptionsRowToggle={onToolOptionsRowToggle}
						onToggleFreehandTool={onToggleFreehandTool}
					/>,
				)
				return container
			},
		}
		const topHeader = new UI.Components.ModularHeader({
			dataElement: "custom-top-header",
			placement: "top",
			gap: 0,
			grow: 1,
			position: "start",
			stroke: false,
			items: [headerBlock],
		})

		// Add the "Share feedback" button to the default header (mirrors useWebviewer).
		if (!previewFeedbackButtonAddedRef.current) {
			const defaultHeader = UI.getModularHeader("default-top-header")
			if (defaultHeader) {
				const customButton = new UI.Components.CustomButton({
					title: "Share feedback",
					label: "Share feedback",
					dataElement: "feedbackButton",
					onClick: () => openFeedbackPanel(),
				})
				const items = defaultHeader.getItems()
				const searchIndex = items.findIndex(
					(item: { dataElement?: string }) => item.dataElement === "searchPanelToggle",
				)
				items.splice(searchIndex > -1 ? searchIndex : items.length, 0, customButton)
				defaultHeader.setItems(items)
				previewFeedbackButtonAddedRef.current = true
			}
		}

		// Add the custom header without disturbing the existing default header(s).
		UI.addModularHeaders([topHeader])
		UI.enableFeatures([UI.Feature.Measurement])

		// Suppress Apryse's built-in popups — our custom selection toolbar + context menu
		// replace them. `contextMenuPopup` = the right-click tool popup (pan / note /
		// highlight / text); `annotationPopup` = the per-annotation style toolbar shown on
		// selecting a highlight.
		UI.disableElements(["annotationPopup", "contextMenuPopup"])

		// `disableElements` does NOT reliably stop the annotation popup from opening in this
		// WebViewer build (it re-shows itself with visibility:visible on every selection), so
		// also force it closed whenever it becomes visible. The visibilityChanged detail is
		// `{ element, isVisible }`.
		UI.addEventListener(UI.Events.VISIBILITY_CHANGED, (e: { detail?: { element?: string; isVisible?: boolean } }) => {
			if (e?.detail?.element === "annotationPopup" && e.detail.isVisible) {
				UI.closeElements(["annotationPopup"])
			}
		})

		// Disable Apryse's BUILT-IN annotation copy/paste hotkeys. Otherwise Ctrl/Cmd+C copies
		// the selected highlight into Apryse's own clipboard and Ctrl/Cmd+V pastes an offset
		// duplicate annotation — producing a second, mis-placed object on top of our real
		// duplicate. Our window keydown listener handles copy/paste independently.
		UI.hotkeys.off(UI.hotkeys.Keys.CTRL_C)
		UI.hotkeys.off(UI.hotkeys.Keys.COMMAND_C)
		UI.hotkeys.off(UI.hotkeys.Keys.CTRL_V)
		UI.hotkeys.off(UI.hotkeys.Keys.COMMAND_V)

		// Apryse's default `z` hotkey switches to zoom even when focus is inside our React sidebars.
		// Disable it; the custom header owns typing-aware z+/z- zoom shortcuts.
		UI.hotkeys.off("z")

		// REQ-395/396: `t` is Apryse's own bare shortcut for its FreeText tool (`UI.hotkeys.Keys.T`,
		// same reserved list as a/h/s/z — see AGENTS.md's "Tools and the split screen" hotkeys
		// section). This app never binds a note-tool hotkey of its own (no dedicated bare-letter
		// shortcut is confirmed against the Figma frames for this issue), but left unguarded, a bare
		// `t` press would still arm Apryse's RAW FreeText tool outside `noteTool.store` entirely —
		// producing a native annotation this app never tracks, persists, or can select/delete through
		// its own flow (a dangling, unstyled note gone on reload). Disabling the key removes that
		// footgun; the toolbar button is the only way to arm the tool.
		UI.hotkeys.off("t")

		// REQ-400: `r` / `o` are Apryse's own bare shortcuts for its Rectangle / Ellipse annotation
		// tools (in the same reserved list as a/h/s/c — see AGENTS.md). off() is included here for the
		// same defensive reason as the z one above, but per the a/h/s/c comment below it is NOT
		// expected to fully suppress a TOOL's own shortcut (only a hotkey-map entry like z's). #1381
		// review moved Rectangle/Ellipse OFF their own bare letters (menu digit-pick only, see
		// hotkeyRegistry), so R/O no longer arm anything of ours — but left unguarded, Apryse's own
		// listener would still arm its RAW create tool on a bare press, bypassing our styling/backend
		// flow entirely (that tool only gets wired up when armShapeTool sets activeShapeTool). The real
		// fix is the same one a/h/s/c use: hotkeys/useEditorHotkeys binds KeyR/KeyO in the CAPTURE
		// phase so the event never reaches Apryse's listener — just swallowed now, with nothing left
		// for them to do.
		UI.hotkeys.off("r")
		UI.hotkeys.off("o")

		// `f` is Apryse's own bare shortcut for its RAW FreeHand (draw) tool — NOT the Pen tool below,
		// which is REQ-400's own freehand highlight flow on `p`. A bare `f` armed a tool whose strokes
		// nothing here tracks or persists, and it is an easy key to reach for instinctively when you
		// want to draw (PR #1386 review), so it is swallowed the same way as r/o: off() here for the
		// hotkey-map case, plus the capture-phase binding in hotkeys/useEditorHotkeys that stops the
		// event before Apryse's own listener.
		UI.hotkeys.off("f")

		// REQ-400 #1324: `p` is Apryse's own bare shortcut for its pan tool, and the Pen tool now
		// claims that key (PR #1392 review). Same defensive-only status as the r/o calls above — the
		// binding that actually wins is hotkeys/useEditorHotkeys' CAPTURE-phase entry for
		// `beatsApryseTool`, which stops the event before Apryse's listener ever sees it.
		UI.hotkeys.off("p")

		// Apryse also binds bare A / H / S / C to its arrow, highlight, signature and crop tools, which
		// collided with the header-menu shortcuts (REQ-359 follow-up, #1381 review): pressing A opened
		// our + Add menu AND armed the arrow tool. That is NOT fixed here, and two attempts to fix it
		// here failed — `UI.hotkeys.off("a")` (a tool's shortcut does not live in the hotkey map) and
		// `UI.disableTools([...])` (which, despite documenting that it "also disable[s] the
		// corresponding hotkeys", left the key working — and stripped the three buttons from the
		// PREVIEW toolbar as a side effect, since preview re-enables Apryse's annotate groups).
		// It is fixed in hotkeys/useEditorHotkeys instead, by binding those four in the CAPTURE phase
		// so the event never reaches Apryse's listener. Don't reach for a UI.* lever here again.

		customHeaderAddedRef.current = true
	}

	if (modeRef.current === "editor") {
		// Editor: show only the custom header, hide the default Apryse header + tools row,
		// hide the default page-nav pills (the modular UI renders them in the
		// "page-nav-floating-header" FloatingHeader, with "page-controls-container" inside; the
		// custom PageNavigationBar replaces them), and use the annotation edit tool so clicks
		// select highlights.
		UI.disableElements(["default-top-header", "tools-header", "page-nav-floating-header", "page-controls-container"])
		UI.enableElements(["custom-top-header"])
		UI.enableElements(PREVIEW_DISABLED_ELEMENTS)
		setToolModeOnAllPanes(instance, Tools.ToolNames.EDIT)
		return
	}

	// Preview: hide the custom header, restore the default Apryse header (+ tools row) and the
	// default page-nav pills, hide editor affordances, close any editor sidebars, use the pan tool.
	UI.disableElements(["custom-top-header"])
	UI.enableElements(["default-top-header", "tools-header", "page-nav-floating-header", "page-controls-container"])
	UI.disableElements(PREVIEW_DISABLED_ELEMENTS)
	UI.closeElements(["elevation-references-panel", "reference-panel"])
	setIsPanelOpen(false)
	setIsReferencePanelOpen(false)
	setToolModeOnAllPanes(instance, Tools.ToolNames.PAN)
}
